import { Inject, Injectable } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { SplugDefinitionRemoteControllerService } from 'core/api';
import { ISplugValidationDto } from 'core/dto';
import { SnackbarService } from 'core/services';
import { SplugConfigService } from 'core/splug';
import { DsgDeepTranslationKey } from 'projects/designer/src/app/pipes/translate.pipe';
import { BehaviorSubject, Subject, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { ISplugParametersConfiguration } from '../../../../../remotes/remote-splug-parameters-configuration.service';
import { IActorSplugDialogData } from './actor-splug-info.component';

@Injectable()
export class ActorSplugInfoService {

  private readonly _destroy = new Subject<void>()

  readonly accordionSubject = new BehaviorSubject<MatAccordion>(undefined);
  readonly actorId = this.data.actorId;

  get activityId() {
    return this.actor.activityId;
  }
  get actor() {

    return this.data.actor
  }
  readonly splugFormArray = new FormArray<any>([]);
  readonly formsValidObs = this.splugFormArray.statusChanges.pipe(
    map(status => status === 'VALID'),
    distinctUntilChanged()
  )
  readonly validationResultSubject = new BehaviorSubject<{ [splugId: string]: ISplugValidationDto }>({})
  readonly submittedAllSubject = new BehaviorSubject<boolean>(false);


  private readonly _remoteSplugParametersConfigurationService = this.data.remoteSplugParametersConfigurationService;
  readonly splugDefinitionsSubject = this._splugConfigService.splugDefinitionsSubject;

  readonly splugParametersConfigurationsSubject = this.data.signProcessWizardService.splugParametersConfigurationsSubject;
  readonly actorSplugParamsObs = this.splugParametersConfigurationsSubject.pipe(
    map((actorParams) => actorParams.filter(({ actorId }) => this.actorId === actorId))
  )

  readonly validSplugsObs = this.validationResultSubject.pipe(
    filter(Boolean),
    map((validationResult) => Object.entries(validationResult).reduce<{ [splugId: string]: boolean }>((total, [splugId, validationResult]) => ({ ...total, [splugId]: validationResult.status === 'OK' }), {}))
  )

  readonly initalSplugFormDataSubject = new BehaviorSubject<{ [splugId: string]: any }>({})
  readonly splugFormGroup: { [splug: string]: FormGroup } = {}

  readonly selectedSplugsSubject = new BehaviorSubject<{ [splugId: string]: boolean }>({})
  get selectedSplugs() {

    return this.selectedSplugsSubject.value;
  }
  get selectedSplugsArray() {
    return Object.entries(this.selectedSplugs)
      .filter(([, selected]) => selected)
      .map(([splugId]) => splugId)
  }

  get selectedForms() {
    return Object.entries(this.splugFormGroup)
      .filter(([formSplugId]) => this.selectedSplugsArray.includes(formSplugId))
      .reduce<{ [splug: string]: FormGroup }>((total, [key, value]) => {
        total[key] = value;
        return total
      },
        {}
      )
  }


  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: IActorSplugDialogData,
    private readonly _splugDefinitionRemoteControllerService: SplugDefinitionRemoteControllerService,
    private readonly _splugConfigService: SplugConfigService,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,

  ) {


    this.actorSplugParamsObs
      .pipe(
        filter((actorSplugParams) => Boolean(actorSplugParams?.length)),
        map((actorSplugParams) => actorSplugParams.reduce((total, actorSplugParams) => ({
          ...total,
          [actorSplugParams.sigTypeId]: actorSplugParams.sigTypeParam
        }),
          {}
        )),
        takeUntil(this._destroy)
      )
      .subscribe((actorSplugParams) => {

        this.initalSplugFormDataSubject.next(actorSplugParams);
      })


    this.actorSplugParamsObs.pipe(
      takeUntil(this._destroy),
      map((actorSplugParams) => actorSplugParams.map)
    )
      .subscribe()
  }

  validateAndSave() {

    return this.validate()
      .pipe(
        take(1),
        filter(({ isValid }) => isValid),
        switchMap(({ actorSplugsWithParams }) => this.save(actorSplugsWithParams))
      )

  }

  save(actorSplugWithParams: ISplugParametersConfiguration[]) {

    return this._remoteSplugParametersConfigurationService.insertBatch(actorSplugWithParams)
      .pipe(
        this._snackbarService.handleAsync({ errorMessage: 'ss-entities.actor.insert-splug.error', successMessage: 'ss-entities.actor.insert-splug.success' })
      )
  }






  validate() {

    const anyFormInvalid = !!Object.values(this.selectedForms)
      .filter((form) => !form.valid)
      .length

    if (anyFormInvalid) {
      this.submittedAllSubject.next(true);
      return of({ isValid: false, actorSplugsWithParams: null });
    }

    const formsWithData = Object.entries(this.splugFormGroup)
      .filter(([splugId, formGroup]) => {

        const hasSomeValues = Object.values(formGroup?.value).filter((value) => value !== undefined && value !== null).length > 0
        return hasSomeValues && this.selectedSplugs[splugId]
      })

    if (formsWithData.length === 0) {
      // this.data.actorSplugInfoDialogService.close();
      return of(null)
    }


    return combineLatest(formsWithData.map(([splugId, form]) => {

      const keyValuePairs = Object.entries(form.value).map(([key, value]) => ({
        key,
        value: this._splugConfigService.executeStringifer(splugId, key, value)
      }))

      return this._splugDefinitionRemoteControllerService.validateSplugParams(splugId, keyValuePairs)
        .pipe(
          map((validationResult) => ({ splugId, validationResult, formData: form.value })),
        )
    }))
      .pipe(
        tap((validations) => {
          const validationResult = validations.reduce<{ [splugId: string]: ISplugValidationDto }>((total, { splugId, validationResult }) => {
            total[splugId] = validationResult;
            return total;
          },
            {}
          );
          this.validationResultSubject.next(validationResult);
        }),
        map((validations) => ({
          isValid: !validations.find(({ validationResult }) => validationResult.status !== 'OK'),
          actorSplugsWithParams: validations.map<ISplugParametersConfiguration>(({ formData, splugId }) => ({
            actorId: this.actorId,
            sigTypeParam: formData,
            sigTypeId: splugId,
          }))
        }))
      );

  }


  selectSplug(splugId: string) {

    this.selectedSplugsSubject.next({
      ...this.selectedSplugs,
      [splugId]: true
    })
  }

  deselectSplug(splugId: string) {

    const prev = this.selectedSplugs[splugId];

    if (!prev) {
      return;
    }

    this.selectedSplugsSubject.next({
      ...this.selectedSplugs,
      [splugId]: false
    })
  }


  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }

}


